import axios from 'axios';
import cn from 'classnames';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useEffect, useState } from 'react';
import { customDataLayerPush, obscureString } from 'src/utils/analytics';
import { LABEL, DL_EVENT, UTM, platformID } from 'src/utils/analytics/constants';
import { Hubspot } from '../../../constants/destinations';
import ThankYou from '../../../images/business-intelligence/thank-you.svg';
import { validateEmail } from '../../../utils/email';
import Button, { ButtonSize, ButtonVariant } from '../../Button';
import FormErrorMessage from '../../FormErrorMessage';
import Loading from '../../Loading';
import TermsAndConditions from '../../TermsAndConditions';
import UIPill from '../../UIPill';
import { validateDomain } from '_utils/suffix';
import { sendToHubspot } from '_services/hubspot';

interface DataConsultFormProps {
  buttonText?: string;
  successText?: string;
  consultData?: any;
  termsAndConditions?: string;
}

const DataConsultForm: React.FC<DataConsultFormProps> = props => {
  const { t } = useTranslation();
  const { language } = useI18next();
  const {
    successText = t('Thank You!'),
    buttonText = t('Schedule a consult'),
    consultData,
    termsAndConditions,
  } = props;

  const [email, setEmail] = React.useState<string>('');
  const [firstName, setFirstName] = React.useState<string>('');
  const [lastName, setLastName] = React.useState<string>('');
  const [company, setCompany] = React.useState<string>('');
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [inputError, setInputError] = React.useState<boolean>(false);
  const [amazonDSP, setAmazonDSP] = useState<{
    title: string;
    value: string;
    selected: boolean;
  }>(null);
  const [serviceList, setServiceList] = useState<
    {
      title: string;
      value: string;
      selected: boolean;
    }[]
  >([]);

  const amazonDSPChoices = [
    { title: t('Yes'), value: 'Yes', selected: false },
    { title: t('No'), value: 'No', selected: false },
  ];

  const services = [
    {
      title: t('Data Integration'),
      value: 'Data Integration',
      selected: false,
    },
    {
      title: t('Custom Reporting'),
      value: 'Custom Reporting',
      selected: false,
    },
    {
      title: t('Amazon Marketing Cloud (AMC)'),
      value: 'Amazon Marketing Cloud (AMC)',
      selected: false,
    },
  ];

  const isValidForm = () => {
    return (
      firstName !== '' &&
      lastName !== '' &&
      validateEmail(email) &&
      validateDomain(email) &&
      company !== '' &&
      serviceList.length !== 0 &&
      amazonDSP !== null
    );
  };

  const clearForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setCompany('');
    setServiceList([]);
    setAmazonDSP(null);
  };

  const submitToHubSpot = async () => {
    const portalId = Hubspot.portalId;
    const formGuid = Hubspot.BIformId;

    const postData = {
      portalId,
      formGuid,
      fields: [
        {
          name: 'firstname',
          value: firstName,
        },
        {
          name: 'lastname',
          value: lastName,
        },
        {
          name: 'email',
          value: email,
        },
        {
          name: 'company',
          value: company,
        },
        {
          name: 'business_intelligence_service_option',
          value: serviceList
            .map(e => {
              if (e.value !== '') {
                return e.value;
              }
            })
            .join(';'),
        },
        {
          name: 'do_you_currently_run_amazon_dsp',
          value: amazonDSP.value,
        },
        {
          name: 'conversion_page_url',
          value: window.location.href,
        },
        {
          name: 'gclid',
          value:
            window?.localStorage?.getItem(platformID.gclid) != null
              ? window?.localStorage?.getItem(platformID.gclid)
              : '',
        },
        {
          name: 'utm_campaign',
          value:
            window?.localStorage?.getItem(platformID.campaignid) != null
              ? window?.localStorage?.getItem(platformID.campaignid)
              : '',
        },
        {
          name: 'fbclid',
          value:
            window?.localStorage?.getItem(platformID.fbclid) != null
              ? window?.localStorage?.getItem(platformID.fbclid)
              : '',
        },
        {
          name: 'li_fat_id',
          value:
            window?.localStorage?.getItem(platformID.li_fat_id) != null
              ? window?.localStorage?.getItem(platformID.li_fat_id)
              : '',
        },
        {
          name: 'utm_content',
          value:
            window?.localStorage?.getItem(UTM.content) != null
              ? window?.localStorage?.getItem(UTM.content)
              : '',
        },
        {
          name: 'utm_medium',
          value:
            window?.localStorage?.getItem(UTM.medium) != null
              ? window?.localStorage?.getItem(UTM.medium)
              : '',
        },
        {
          name: 'utm_source',
          value:
            window?.localStorage?.getItem(UTM.source) != null
              ? window?.localStorage?.getItem(UTM.source)
              : '',
        },
        {
          name: 'utm_term',
          value:
            window?.localStorage?.getItem(UTM.term) != null
              ? window?.localStorage?.getItem(UTM.term)
              : '',
        },
        {
          name: 'hs_language',
          value:
            language === 'en-US'
              ? 'en'
              : language === 'zh-CN'
                ? 'zh'
                : language,
        },
        {
          name: 'google_analytics_client_id',
          value:
            window?.localStorage?.getItem('google_analytics_client_id') != null
              ? window?.localStorage?.getItem('google_analytics_client_id')
              : '',
        },
        {
          name: 'partner_key',
          value:
            window?.localStorage?.getItem('partner_key') != null
              ? window?.localStorage?.getItem('partner_key')
              : '',
        },
        {
          name: 'acquisition_channel_source',
          value:
            window?.localStorage?.getItem('acquisition_channel_source') != null
              ? window?.localStorage?.getItem('acquisition_channel_source')
              : '',
        },
      ],
    };

    setInputError(false);

    const { status } = await sendToHubspot(postData);
    if (status === 200) {
      // AMC + BI; no adSpend, phone, country
          customDataLayerPush(DL_EVENT.formSubmit, {
            formLabel: window.location?.pathname === '/amc-amazon-marketing-cloud/' ? LABEL.AMC : LABEL.BI,
            leadUserData: {
              email: obscureString(email),
              address: {
                first_name: obscureString(firstName),
                last_name: obscureString(lastName),
                postal_code: '12345',
              },
            }
          });

      clearForm();
      setLoading(false);
      setSubmitted(true);
    }
  };

  const buttonOnClick = () => {
    if (isValidForm()) {
      setLoading(true);
      submitToHubSpot();
    } else {
      setInputError(true);
    }
  };

  useEffect(() => {
    if (consultData) {
      setServiceList([
        {
          title:
            consultData && consultData.variant != '' ? consultData.variant : '',
          value:
            consultData && consultData.variant != '' ? consultData.variant : '',
          selected: true,
        },
      ]);
    }
  }, [consultData]);

  const sharedInputClasses =
    'newsletter-signup-input border border-gray-3 rounded-lg focus:border-indigo-1 focus:outline-none focus:ring-4 focus:ring-indigo-3-light py-2 px-4 w-full text-base mt-2';

  const sharedLabelClasses = 'text-base font-semibold';

  return (
    <div
      aria-hidden={true}
      className="max-w-full md:max-w-[600px] mx-auto mt-10"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className={sharedLabelClasses}>{t('First Name')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && firstName.length <= 0 && 'input-error'
            )}
            placeholder={t('First Name')}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Last Name')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && lastName.length <= 0 && 'input-error'
            )}
            placeholder={t('Last Name')}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Work Email')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && (!validateEmail(email) || !validateDomain(email)) && 'input-error'
            )}
            placeholder={t('Work Email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className={sharedLabelClasses}>{t('Company')}</label>
          <input
            className={cn(
              sharedInputClasses,
              inputError && company.length <= 0 && 'input-error'
            )}
            placeholder={t('Company')}
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-6">
        <label className={sharedLabelClasses}>
          {t('Which service are you interested in?')}
        </label>
        <div className="mt-2">
          {services.map(key => {
            return (
              <>
                <UIPill
                  title={key.title}
                  className={cn(
                    inputError && serviceList.length > 0 && 'input-error',
                    'border-l border-r border-y '
                  )}
                  onClick={() => {
                    const cloneserviceList = [...serviceList];

                    if (
                      cloneserviceList.filter(e => e.title === key.title)
                        .length > 0
                    ) {
                      const index = cloneserviceList.findIndex(
                        e => e.title === key.title
                      );
                      if (index > -1) {
                        cloneserviceList.splice(index, 1);
                        setServiceList(cloneserviceList);
                      }
                    } else {
                      cloneserviceList.push(key);
                      setServiceList(cloneserviceList);
                    }
                  }}
                  selected={
                    serviceList.filter(e => e.title === key.title).length > 0
                  }
                />
              </>
            );
          })}
        </div>
      </div>
      <div className="mt-6">
        <label className={sharedLabelClasses}>
          {t('Do you currently run Amazon DSP?')}
        </label>
        <p className="text-base mt-2">
          {t(
            'Currently, AMC is only available for advertisers who run Amazon DSP but will open to all Sponsored Ads customers in 2023.'
          )}
        </p>
        <div className="flex flex-wrap mt-3">
          {amazonDSPChoices?.map((key, index) => {
            return (
              <UIPill
                key={key.title}
                title={key.title}
                className={cn(
                  index == 0
                    ? 'rounded-l-lg sm:rounded-r-none md:mx-0'
                    : index == 1
                      ? 'ounded-r-lg md:ml-[-2px] sm:rounded-l-none md:mx-0'
                      : '',
                  'border-l border-r border-y w-full sm:w-auto !mr-0 sm:mr-5',
                  inputError && amazonDSP === null && 'input-error'
                )}
                onClick={() => {
                  if (amazonDSP !== key) {
                    setAmazonDSP(key);
                  }
                }}
                selected={amazonDSP?.title === key.title}
              />
            );
          })}
        </div>
      </div>
      {inputError && <FormErrorMessage />}

      <Button
        className="mx-auto py-2 px-22 my-10"
        variant={ButtonVariant.primary}
        text={buttonText}
        size={ButtonSize.large}
        onClick={e => {
          buttonOnClick();
        }}
      />

      <TermsAndConditions content={termsAndConditions} />

      {loading && (
        <div className="transition duration-200 absolute top-0 w-full h-full left-0 p-8 bg-white flex items-center justify-center rounded-xl z-50">
          <Loading />
        </div>
      )}
      {submitted && (
        <div className="absolute w-full h-full left-0 top-0 p-8 bg-white flex items-center justify-center rounded-xl z-50">
          <div className="mx-auto flex flex-col gap-8 items-center">
            <ThankYou />
            <h3 className="text-5xl font-semibold max-w-xs text-center leading-10 align-center">
              {successText}
            </h3>
            <p className="mx-auto max-w-xl">
              {t(
                'The Business Intelligence Team will be reaching out to you shortly in order to book a call to explore the right solution for your business.'
              )}
            </p>
            <Button
              className="mx-auto py-2 px-22"
              variant={ButtonVariant.primary}
              text={'Resubmit'}
              size={ButtonSize.large}
              onClick={e => {
                setSubmitted(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DataConsultForm;
